import {
  Button,
  Code,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  useDisclosure,
  useToast
} from "@chakra-ui/react"
import React, { useEffect, useState } from "react"
import { Center, VStack } from "@chakra-ui/layout"
import { ModalCloseButton } from "@chakra-ui/modal"
import CarrotClient from "../../lib/carrot-api"
import Settings from "../../utils/CarrotConfig"
import ChangeHealthcareResponseTable from "./ChangeHealthcareResponseTable"
import DeductibleOopSummary from "./DeductibleOopSummary"
import changeHealthcare401Redirect from "./ChangeHealthcare401Redirect"
const client = new CarrotClient(Settings.CARROT_BACKEND_URL)

type FileViewerModalButtonProps = {
  title: string
  id: number
}

export default function FileViewerModalButton({ title, id }: FileViewerModalButtonProps): JSX.Element {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [responseData, setResponseData] = useState<any>()
  const [displayType, setDisplayType] = useState<string>("table")
  const [shouldHideTableButton, setShouldHideTableButton] = useState<boolean>(false)
  const [shouldHideSummaryButton, setShouldHideSummaryButton] = useState<boolean>(false)
  const [loadedId, setLoadedId] = useState<number>(id)
  const toast = useToast()

  const viewTable = () => setDisplayType("table")
  const viewJson = () => setDisplayType("json")
  const viewSummary = () => setDisplayType("summary")

  useEffect(() => {
    const loadResponseData = async () => {
      if (title === "Request") {
        try {
          const data = await client.getChangeHealthcareRequest(id)
          setResponseData(data)
        } catch (e) {
          changeHealthcare401Redirect(e)
          throw e
        }
      } else if (title === "Response") {
        try {
          const data = await client.getChangeHealthcareResponse(id)
          setResponseData(data)
        } catch (e) {
          changeHealthcare401Redirect(e)
          throw e
        }
      }
    }

    if (isOpen && (!responseData || loadedId !== id)) {
      loadResponseData()
      setLoadedId(id)
    }
  }, [id, isOpen, responseData, loadedId, title])

  // For response files, if the response doesn't include `benefitsInformation` we want to force display
  // of the JSON view and hide the table button since we'll be unable to parse benefit lines out of the response.
  useEffect(() => {
    if (title === "Response" && isOpen && responseData && responseData.benefitsInformation === undefined) {
      setDisplayType("json")
      setShouldHideTableButton(true)
      setShouldHideSummaryButton(true)
    }
  }, [title, responseData, isOpen])

  return (
    <>
      <Button m={1} minWidth="80px" size="xs" colorScheme="carrotOrange" onClick={onOpen}>
        {title}
      </Button>

      <Modal isOpen={isOpen} onClose={onClose} size={"xl"}>
        <ModalOverlay />
        <ModalContent width={"fit-content"} maxW="90rem" minW="20rem" minH="10rem">
          <ModalHeader>
            {title}
            <Center>
              {title === "Response" && (
                <>
                  {!shouldHideTableButton && (
                    <Button ml={2} size="sm" colorScheme="carrotOrange" mr={3} onClick={viewTable}>
                      Table
                    </Button>
                  )}
                  {!shouldHideSummaryButton && (
                    <Button size="sm" colorScheme="carrotOrange" mr={3} onClick={viewSummary}>
                      Summary
                    </Button>
                  )}
                  <Button size="sm" colorScheme="carrotOrange" onClick={viewJson}>
                    JSON
                  </Button>
                </>
              )}
            </Center>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {!responseData ? (
              <Center maxW="80rem" minW="20rem" minH="20rem">
                <Spinner />
              </Center>
            ) : title === "Response" && displayType === "table" ? (
              <ChangeHealthcareResponseTable response={responseData} />
            ) : title === "Response" && displayType === "summary" ? (
              <DeductibleOopSummary response={responseData} />
            ) : (
              <Center>
                <VStack>
                  <Button
                    size={"sm"}
                    variant="outline"
                    onClick={() => {
                      navigator.clipboard.writeText(JSON.stringify(responseData, null, 4))
                      toast({
                        title: "Success",
                        description: "The JSON content has been copied to your clipboard.",
                        status: "success",
                        position: "top-right",
                        duration: 2000,
                        isClosable: false
                      })
                    }}
                  >
                    Copy text to clipboard
                  </Button>
                  <Code>
                    <pre style={{ maxWidth: "60rem", whiteSpace: "pre-wrap" }}>
                      {JSON.stringify(responseData, null, 4)}
                    </pre>
                  </Code>
                </VStack>
              </Center>
            )}
          </ModalBody>

          <ModalFooter>
            <Button colorScheme="carrotOrange" onClick={onClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}
